import { Component, DestroyRef, ViewEncapsulation, computed, effect, inject, input, output } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, FormGroup, ValidatorFn } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CfiRecordViewComponent, StudentAwardService } from '@k12/app-components';
import { LoadingSpinnerService } from '@k12/app-services';
import { ESA_SECTIONS, ESA_OPTIONS, publicSchoolDistrictOptions } from '../student-esa.config';
import { EMPTY_CLASS_WALLET_TOTALS, type ClassWalletTotals } from '../class-wallet-totals';
import { StudentEsa, ProgramType, ProgramTypes, FieldOption } from '@k12/models';
import { createEnrollmentApply } from '../../enrollment-apply';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'student-esa-detail',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [ReactiveFormsModule, CfiRecordViewComponent],
  template: `
    <cfi-record-view
      title="ESA+"
      [sections]="sections"
      [form]="form"
      [options]="options"
      [columns]="4"
      [canEdit]="isAdmin()"
      [showResendAwardOfferButton]="showResendAwardOffer()"
      [showEmptyState]="showEmptyState()"
      [emptyStateButtonDisabled]="applyDisabled()"
      [showPrintButton]="isAdmin() && !!studentEsa()"
      prefix="esa"
      (edit)="onEdit()"
      (print)="onPrintApplication()"
      (emptyStateApply)="onEmptyStateApply()"
      (resendAwardOffer)="onResendAwardOffer()" />
  `
})
export class StudentEsaDetailComponent {
  readonly isAdmin = input(false);
  readonly studentEsa = input<StudentEsa | null>(null);
  /** K12.Person id of the student — the application subject (Application.OnBehalfOfPersonId). */
  readonly personId = input<string | null>(null);
  /** The award year the household is viewing; used to probe/create the ESA+ enrollment application. */
  readonly academicYearId = input<number | null>(null);
  readonly classWalletTotals = input<ClassWalletTotals>(EMPTY_CLASS_WALLET_TOTALS);
  readonly awardUpdated = output<StudentEsa>();
  readonly printApplication = output<void>();

  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly studentAwardService = inject(StudentAwardService);
  private readonly spinnerService = inject(LoadingSpinnerService);
  private readonly destroyRef = inject(DestroyRef);

  readonly showEmptyState = computed(() => !this.studentEsa());
  readonly showResendAwardOffer = computed(
    () => this.isAdmin() && (this.studentEsa()?.esaAwardStatus ?? '').toLowerCase() === 'unresponsive'
  );

  // Apply is only offered when the ESA+ enrollment period for the selected year is open and the
  // application subject (personId) is known. Availability is a single reactive stream.
  private readonly applyController = createEnrollmentApply({
    programType: ProgramTypes.EsaPlusApplication,
    showEmptyState: this.showEmptyState,
    academicYearId: this.academicYearId,
    personId: this.personId
  });
  readonly applyDisabled = this.applyController.applyDisabled;

  readonly sections = ESA_SECTIONS;
  public options: Record<string, FieldOption[]> = {
    ...ESA_OPTIONS,
    disabilityTypes: [...ESA_OPTIONS['disabilityTypes']],
    inEligibilityReasons: [...ESA_OPTIONS['inEligibilityReasons']]
  };
  readonly form: FormGroup = this.fb.group(
    ESA_SECTIONS.flatMap((s) => s.fields).reduce<
      Record<string, [{ value: null; disabled: boolean } | null, ValidatorFn[]]>
    >((acc, field) => ({ ...acc, [field.key]: [field.readonly ? { value: null, disabled: true } : null, []] }), {})
  );

  private readonly studentId = this.route.snapshot.paramMap.get('studentId') ?? '';

  constructor() {
    this.populateDropdownOptions();
    effect(() => {
      const esa = this.studentEsa();
      // Rebuilt per record rather than once at construction: a value saved while this field was
      // free text needs to be offered as its own option, or the view renders it as a dash.
      this.options['publicSchoolDistricts'] = publicSchoolDistrictOptions(esa?.esaPublicSchoolDistrict);
      this.form.patchValue({
        ...esa,
        disabilityType1Id: esa?.disabilityType1Id ?? '',
        disabilityType2Id: esa?.disabilityType2Id ?? '',
        ...this.classWalletTotals()
      });
    });
  }

  private populateDropdownOptions(): void {
    const studentData = this.route.parent?.snapshot.data['student'] ?? this.route.snapshot.data['student'];

    const disabilityTypes = studentData?.disabilityTypes ?? [];
    const inEligibilityReasons = studentData?.inEligibilityReasons ?? [];

    this.options['disabilityTypes'] = [
      { label: 'None', value: '' },
      ...disabilityTypes.map((disability: { name: string; id: string | number }) => ({
        label: disability.name,
        value: disability.id
      }))
    ];

    this.options['inEligibilityReasons'] = [
      { label: 'None', value: null },
      ...inEligibilityReasons.map((reason: { name: string; id: number }) => ({
        label: reason.name,
        value: reason.id
      }))
    ];
  }

  onEmptyStateApply(): void {
    this.applyController.apply();
  }

  onEdit(): void {
    this.router.navigate(['/student', this.studentId, 'esaedit']);
  }

  onResendAwardOffer(): void {
    this.spinnerService.showSpinner();
    this.studentAwardService
      .resendAwardOffer(this.studentId, ProgramType.ESA)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.spinnerService.hideSpinner();
          // Resend is only offered when an award record exists, so there is nothing to patch otherwise.
          const current = this.studentEsa();
          if (!current) {
            return;
          }
          this.awardUpdated.emit({
            ...current,
            esaAwardStatus: response.esaAwardStatus ?? current.esaAwardStatus
          });
        },
        error: () => this.spinnerService.hideSpinner()
      });
  }

  onPrintApplication(): void {
    this.printApplication.emit();
  }
}
